import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { BarChart, Bar, PieChart, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Search, Bell, Clock, Zap, Users, FolderOpen, LayoutTemplate, Box, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

const Home = () => {
  const navigate = useNavigate();


  // State variables
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Dashboard Data State
  const [stats, setStats] = useState({
    clients: 0,
    reports: 0,
    templates: 0,
    categories: 0
  });

  const [recentReports, setRecentReports] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [pipelineData, setPipelineData] = useState([]);
  const [clientProfitData, setClientProfitData] = useState([]);
  const [projectWiseData, setProjectWiseData] = useState([]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/home`, { withCredentials: true });

      if (userRes.status === 200 && userRes.data.user) {
        setUser(userRes.data.user);
        localStorage.setItem('user', userRes.data.user.user_name);
      } else {
        navigate('/login');
        return;
      }

      // Fetch all required data in parallel
      const [clientsRes, reportsRes, templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${process.env.REACT_APP_API_URL}/api/templates`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${process.env.REACT_APP_API_URL}/api/categories`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      const clients = clientsRes.data || [];
      const reports = reportsRes.data || [];
      const templates = templatesRes.data || [];
      const categories = categoriesRes.data || [];

      setStats({
        clients: clients.length,
        reports: reports.length,
        templates: templates.length,
        categories: categories.length
      });

      // Prepare Recent Reports (last 5)
      const sortedReports = [...reports].reverse().slice(0, 5);
      setRecentReports(sortedReports.map(r => {
        const client = clients.find(c => c.client_id === r.client_id);
        return {
          id: r.report_id,
          name: r.report_name,
          clientName: client ? client.client_name : 'Unknown Client',
          date: new Date().toLocaleDateString()
        };
      }));

      // Prepare Chart Data: Reports per Client
      const clientReportCounts = {};
      reports.forEach(r => {
        const client = clients.find(c => c.client_id === r.client_id);
        const cName = client ? client.client_name : 'Unknown';
        clientReportCounts[cName] = (clientReportCounts[cName] || 0) + 1;
      });

      const processedChart = Object.keys(clientReportCounts).map(key => ({
        name: key.length > 12 ? key.substring(0, 12) + '...' : key,
        fullTitle: key,
        value: clientReportCounts[key]
      })).sort((a, b) => b.value - a.value).slice(0, 6); // Top 6 clients

      setChartData(processedChart);

      // Project Wise Chart (Pie Chart by Template)
      const templateCounts = {};
      reports.forEach(r => {
        const t = templates.find(temp => temp.template_id === r.template_id);
        const tName = t ? t.template_name : 'Custom';
        templateCounts[tName] = (templateCounts[tName] || 0) + 1;
      });
      setProjectWiseData(Object.keys(templateCounts).map(k => ({ name: k, value: templateCounts[k] })));

      // Client Profit Mockup Data (Area Chart)
      // Since actual profit isn't stored as a single field, we'll generate a realistic-looking trend based on clients
      const profitData = clients.slice(0, 6).map((c, i) => {
        const reportCount = reports.filter(r => r.client_id === c.client_id).length || 1;
        return {
          name: c.client_name.substring(0, 10),
          fullTitle: c.client_name,
          profit: Math.round((Math.random() * 50 + 20) * reportCount) // Fake profit in Lakhs
        };
      });
      setClientProfitData(profitData);

      // Pipeline Mockup Data
      // Mocking status since there is no 'status' column in hospital_reports
      const total = reports.length || 10;
      setPipelineData([
        { stage: 'Proposals Sent', count: total + 15, color: 'bg-indigo-500' },
        { stage: 'In Progress', count: Math.round(total * 0.6) + 5, color: 'bg-amber-500' },
        { stage: 'Finalized Reports', count: total, color: 'bg-emerald-500' }
      ]);

    } catch (err) {
      console.error("Auth / Dashboard Fetch error:", err);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const COLORS = ['#10b981', '#0ea5e9', '#6366f1', '#f59e0b', '#8b5cf6', '#ec4899'];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/90 border border-slate-800 p-3 rounded-xl shadow-2xl backdrop-blur-md">
          <p className="text-white font-semibold text-sm mb-1">{payload[0].payload.fullTitle}</p>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <p className="text-emerald-400 font-mono text-xs">
              {payload[0].value} Reports
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Framer Motion Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-400 rounded-full"
        />
        <p className="mt-6 text-sm text-gray-400 tracking-widest font-mono animate-pulse uppercase">
          Loading...
        </p>
      </div>
    );
  }

  const headerExtras = (
    <>
      <div className="relative hidden md:block">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-64 bg-slate-900/50 border border-slate-800 rounded-full py-2 pl-10 pr-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all placeholder:text-gray-600"
        />
      </div>
      <button className="relative p-2 rounded-full bg-slate-900/50 border border-slate-800 hover:bg-slate-800 transition-colors text-gray-400 hover:text-white group">
        <Bell className="w-5 h-5 group-hover:animate-wiggle" />
        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border border-slate-900"></span>
      </button>
    </>
  );

  return (
    <Layout title="Command Center" subtitle="Real-time strategic oversight & operational analytics" headerExtras={headerExtras}>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="space-y-6"
      >

        {/* WELCOME SECTION */}
        <motion.div variants={itemVariants} className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-gradient-to-r from-emerald-900/20 via-slate-900/20 to-indigo-900/20 border border-slate-800/50 rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 blur-[100px] rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-500/10 blur-[100px] rounded-full"></div>

          <div className="relative z-10">
            <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">
              Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">{user?.user_name || 'Admin'}</span>
            </h1>
            <p className="text-sm text-gray-400">Here is the latest status of your financial projects and clients.</p>
          </div>
          <div className="relative z-10 flex gap-3">
            <button onClick={() => navigate('/hospital-report')} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)] rounded-xl text-sm font-bold text-slate-950 transition flex items-center gap-2">
              <Zap className="w-4 h-4" />
              New Report
            </button>
          </div>
        </motion.div>

        {/* KPI CARDS */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div onClick={() => navigate('/clients')} className="cursor-pointer bg-slate-900/40 border border-slate-800 rounded-2xl p-5 hover:bg-slate-900/60 transition group relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition"></div>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Clients</p>
                <h3 className="text-2xl font-black text-white font-mono">{stats.clients}</h3>
              </div>
            </div>
          </div>

          <div onClick={() => navigate('/hospital-report')} className="cursor-pointer bg-slate-900/40 border border-slate-800 rounded-2xl p-5 hover:bg-slate-900/60 transition group relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-indigo-500/5 rounded-full blur-2xl group-hover:bg-indigo-500/10 transition"></div>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-indigo-500/10 text-indigo-400 rounded-xl">
                <FolderOpen className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Project Reports</p>
                <h3 className="text-2xl font-black text-white font-mono">{stats.reports}</h3>
              </div>
            </div>
          </div>

          <div onClick={() => navigate('/templates')} className="cursor-pointer bg-slate-900/40 border border-slate-800 rounded-2xl p-5 hover:bg-slate-900/60 transition group relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-rose-500/5 rounded-full blur-2xl group-hover:bg-rose-500/10 transition"></div>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-rose-500/10 text-rose-400 rounded-xl">
                <LayoutTemplate className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Templates Designed</p>
                <h3 className="text-2xl font-black text-white font-mono">{stats.templates}</h3>
              </div>
            </div>
          </div>

          <div onClick={() => navigate('/category-master')} className="cursor-pointer bg-slate-900/40 border border-slate-800 rounded-2xl p-5 hover:bg-slate-900/60 transition group relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl group-hover:bg-amber-500/10 transition"></div>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-amber-500/10 text-amber-400 rounded-xl">
                <Box className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Master Categories</p>
                <h3 className="text-2xl font-black text-white font-mono">{stats.categories}</h3>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CHARTS ROW 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* BAR CHART: Reports by Client */}
          <motion.div variants={itemVariants} className="lg:col-span-2 bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                Reports Distribution by Client
              </h2>
            </div>

            <div className="h-72 w-full">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#475569"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke="#475569"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => Math.floor(value)}
                    />
                    <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: '#0f172a' }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={50}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-sm">
                  <BarChart className="w-8 h-8 opacity-20 mb-2" />
                  No reports available yet.
                </div>
              )}
            </div>
          </motion.div>

          {/* PROJECT WISE PIE CHART */}
          <motion.div variants={itemVariants} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                Project Wise Distribution
              </h2>
            </div>
            <p className="text-xs text-slate-500 mb-4">By Template Type</p>

            <div className="flex-1 w-full min-h-[250px]">
              {projectWiseData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={projectWiseData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {projectWiseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '0.75rem', color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                    />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#cbd5e1' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <p className="text-sm">No project data</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* CHARTS ROW 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* CLIENT PROFIT AREA CHART */}
          <motion.div variants={itemVariants} className="lg:col-span-2 bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                Estimated Client Profit (₹ Lakhs)
              </h2>
            </div>

            <div className="h-72 w-full">
              {clientProfitData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clientProfitData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#475569"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke="#475569"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '0.75rem', color: '#fff' }}
                      itemStyle={{ color: '#10b981', fontWeight: 'bold' }}
                    />
                    <Area type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorProfit)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-sm">
                  No profit data available yet.
                </div>
              )}
            </div>
          </motion.div>

          {/* PROJECT PIPELINE & RECENT */}
          <div className="flex flex-col gap-6">
            {/* PIPELINE */}
            <motion.div variants={itemVariants} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 flex flex-col">
              <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                Project Pipeline
              </h2>

              <div className="flex-1 flex flex-col justify-center space-y-4">
                {pipelineData.map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-xs font-bold text-slate-300 mb-1.5">
                      <span>{item.stage}</span>
                      <span className="text-white font-mono">{item.count}</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: `${Math.max(10, (item.count / pipelineData[0].count) * 100)}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RECENT REPORTS LIST */}
            <motion.div variants={itemVariants} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 flex flex-col flex-1">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-500" />
                  Recent Projects
                </h2>
                <button onClick={() => navigate('/hospital-report')} className="text-xs text-emerald-400 hover:text-emerald-300 transition">
                  View All
                </button>
              </div>

              <div className="flex-1 overflow-auto pr-2 space-y-3 max-h-[160px]">
                {recentReports.length > 0 ? recentReports.map((report) => (
                  <div key={report.id} onClick={() => navigate('/hospital-report')} className="p-2.5 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-xl transition cursor-pointer group">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-bold text-white truncate max-w-[150px] group-hover:text-emerald-400 transition">
                        {report.name}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="truncate">{report.clientName}</span>
                    </div>
                  </div>
                )) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500">
                    <p className="text-sm">No recent reports</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

      </motion.div>
    </Layout>
  );
};

export default Home;
